import type { BoxProps } from '@mui/material/Box';
import type { Theme, SxProps } from '@mui/material/styles';
import type { TypographyProps } from '@mui/material/Typography';

import { useState } from 'react';
import { PackageOpen } from 'lucide-react';
import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

// ----------------------------------------------------------------------

export type EmptyContentProps = React.ComponentProps<'div'> & {
  title?: string;
  imgUrl?: string;
  /** Drawn in place of the image, e.g. a line icon. `null` draws neither. */
  icon?: React.ReactNode;
  filled?: boolean;
  sx?: SxProps<Theme>;
  description?: string;
  action?: React.ReactNode;
  slotProps?: {
    img?: BoxProps<'img'>;
    title?: TypographyProps;
    description?: TypographyProps;
  };
};

export function EmptyContent({
  sx,
  icon,
  imgUrl,
  action,
  filled,
  slotProps,
  description,
  title = 'No data',
  ...other
}: EmptyContentProps) {
  /**
   * Whether the picture asked for can actually be drawn.
   *
   * This used to point at an svg that is not in the repository, and an app
   * that answers an unknown path with index.html hands the browser a web page
   * where it expects an image. Every empty state in the product drew the
   * broken image glyph, which is the worst possible thing to show somebody
   * whose account is simply new.
   */
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  // Whichever source actually reaches the element, since slotProps is spread
  // over imgUrl and a caller may give only that one.
  const source = slotProps?.img?.src ?? imgUrl;
  // Tied to the url that failed, not to the component. A caller that later
  // hands over a working picture would otherwise keep the drawn mark for good.
  const imageBroken = !!source && brokenUrl === source;

  return (
    <ContentRoot filled={filled} sx={sx} {...other}>
      {icon !== undefined ? (
        icon
      ) : source && !imageBroken ? (
        <Box
          component="img"
          alt=""
          src={imgUrl}
          {...slotProps?.img}
          // After the spread, not before. A caller's own onError would
          // otherwise replace this one and a broken picture would stay on
          // screen. Theirs still runs.
          onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
            slotProps?.img?.onError?.(event);
            setBrokenUrl(source);
          }}
          sx={[
            {
              width: 1,
              maxWidth: 160,
            },
            ...(Array.isArray(slotProps?.img?.sx) ? slotProps.img.sx : [slotProps?.img?.sx]),
          ]}
        />
      ) : (
        // Drawn rather than fetched, so there is no file to go missing.
        <Box aria-hidden sx={{ display: 'flex', color: 'text.disabled', opacity: 0.6 }}>
          <PackageOpen size={64} strokeWidth={1.25} />
        </Box>
      )}

      {title && (
        <Typography
          variant="h6"
          {...slotProps?.title}
          sx={[
            {
              mt: 1,
              textAlign: 'center',
              color: 'text.disabled',
              textWrap: 'balance',
            },
            ...(Array.isArray(slotProps?.title?.sx) ? slotProps.title.sx : [slotProps?.title?.sx]),
          ]}
        >
          {title}
        </Typography>
      )}

      {description && (
        <Typography
          variant="body2"
          {...slotProps?.description}
          sx={[
            {
              mt: 1,
              textAlign: 'center',
              color: 'text.disabled',
              textWrap: 'pretty',
            },
            ...(Array.isArray(slotProps?.description?.sx) ? slotProps.description.sx : [slotProps?.description?.sx]),
          ]}
        >
          {description}
        </Typography>
      )}

      {action && action}
    </ContentRoot>
  );
}

// ----------------------------------------------------------------------

const ContentRoot = styled('div', {
  shouldForwardProp: (prop: string) => !['filled', 'sx'].includes(prop),
})<Pick<EmptyContentProps, 'filled'>>(({ filled, theme }) => ({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(0, 3),
  ...(filled && {
    borderRadius: Number(theme.shape.borderRadius) * 2,
    backgroundColor: varAlpha(theme.vars.palette.grey['600Channel'], 0.04),
    border: `dashed 1px ${varAlpha(theme.vars.palette.grey['600Channel'], 0.08)}`,
  }),
}));
